import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const Invoicing = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">How PTS pricing fits together</Typography>
      <Typography>
        A <strong>tariff</strong> is a rate card — base charge, per-mile, per
        escort, mobility uplifts, out-of-hours bands, waiting time, and
        cancellation/aborted-journey charges. Every organisation uses the
        tenant's default tariff unless a specific one is set on its{' '}
        <DocLink
          url="/patient-transport/organisations"
          text="Organisation"
          docsPage
        />{' '}
        record. A <strong>quote</strong> is simply a saved, priced snapshot of
        a booking's journeys — it is never a separately re-typed record, so
        accepting a quote and later auto-pricing the invoice use the same
        tariff logic.
      </Typography>
      <Typography>
        Configure tariffs from{' '}
        <DocLink
          url="/site-configuration/pts-pricing"
          text="Configuration > Patient Transport > Pricing & Tariffs"
        />
        , where you also set quote validity, default VAT, invoice payment
        terms, whether portal bookings are auto-quoted, the variance flag
        threshold, and the Xero-export defaults covered below. Everything on
        this page — tariffs, quotes, invoices, billing runs — needs the{' '}
        <strong>Co-Ordinator</strong> PTS permission; the one exception is
        invoice approval, covered below.
      </Typography>
      <Typography>
        Mileage rules have a configurable <strong>basis</strong>: charge on
        the patient journey only (pickup → destination), or{' '}
        <strong>base to base</strong> — the full round trip from leaving base
        to returning to it, suited to ad-hoc journeys. Base-to-base pricing
        needs the base location to have a map point. Bases geocode their
        point automatically when saved with a valid address, and the map on{' '}
        <DocLink url="/base-locations" text="Base Locations" />{' '}
        shows every base's pin — click a pin to drag it to the exact spot,
        and look out for the "place on map" warning chip on any base still
        missing a point. If no mileage is charged at all, simply leave the
        mileage rule off the tariff.
      </Typography>
      <Alert severity="info" variant="outlined">
        If base-to-base pricing was asked for but the base has no map point,
        AmbuNet doesn't fail the invoice — it charges the journey distance
        instead and labels the line "Mileage (base-to-base unavailable —
        journey distance only)", so under-charged lines are easy to spot and
        fix.
      </Alert>
      <Typography>
        Mileage can also be controlled per journey. Each journey's billed
        mileage defaults to <strong>tracked</strong> — worked out from the
        map using the tariff's basis — but the <strong>Mileage
        Billing</strong> panel in a booking's Quotes section lets a
        coordinator <strong>set</strong> a fixed mileage instead, with
        clickable suggestions for both the pickup-to-dropoff and base-to-base
        figures. A set mileage overrides the computed distance on every quote
        and invoice for that journey, and its line is labelled "(set
        mileage)" so it's clear the figure was fixed by hand.
      </Typography>
      <Alert severity="info" variant="outlined">
        On a quote, tracked base-to-base mileage is labelled as provisional —
        the billed figure is finalised when the invoice is priced from what
        actually happened. After changing a journey's mileage billing,
        re-price any draft quotes so they pick up the new figure.
      </Alert>
      <Screenshot url="patient-transport/tariffs.png">
        The Pricing &amp; Tariffs settings page showing the tariff list with a
        default tariff badge, and the tariff rule editor open.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Quotes</Typography>
      <Typography>
        From a booking's Quotes section, <strong>Generate Quote</strong> prices
        every journey on the booking from the applicable tariff (estimate
        mode — planned times, not recorded ones). Lines can be edited by hand
        before sending if needed, or re-priced from the tariff to discard
        manual edits. Send the quote by email, or mark it accepted directly;
        Site/Admin hospital portal users can also accept a sent quote
        themselves.
      </Typography>
      <Alert severity="info" variant="outlined">
        Accepting a quote moves its booking into the planning pool. If an
        organisation's contract requires a quote before planning, an
        unaccepted quote is what's holding the booking back.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Invoices and auto-pricing</Typography>
      <Typography>
        A booking's Invoices section can create an invoice manually, or use{' '}
        <strong>Auto-price from tariff</strong> once journeys are complete —
        this prices from what actually happened (recorded times, waiting
        time, mobility, escorts), not the original estimate. If the booking
        has an accepted quote, the same action also shows the variance
        between what was quoted and what actually happened, flagged when the
        difference is large enough to be worth checking (the threshold is
        configurable on the Pricing &amp; Tariffs page).
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Invoiced bookings are locked</Typography>
      <Typography>
        Once any invoice references a booking, the booking can no longer be
        edited and its journeys can no longer be cancelled — what the
        customer was billed against stays true to the booking record. To
        change an invoiced booking, credit its journey lines in full first:
        the booking unlocks (on a consolidated invoice, crediting that
        booking's own journey lines is enough — the rest of the invoice is
        unaffected), and the next billing run will pick those journeys up
        again and re-bill them. Bookings that are Cancelled or Rejected
        can't be edited either.
      </Typography>
      <Alert severity="info" variant="outlined">
        Every booking edit is audited: a note is added to the booking and a
        log entry records exactly which fields changed, from what to what. If
        a stop's address changes, cached route distances are discarded so any
        later pricing uses the new route — edits can't leave stale mileage
        behind.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Billing runs</Typography>
      <Typography>
        <DocLink url="/pts/invoices" text="PTS Invoices" /> has a{' '}
        <strong>Billing Runs</strong> view for organisations billed on a
        consolidated basis rather than per booking. Pick an organisation and a
        period, then <strong>Preview</strong> — this groups every completed,
        not-yet-invoiced journey in that period the way the organisation's
        contract says to (one invoice per journey, per booking, per patient,
        or one consolidated invoice for the period), and shows exactly what
        each draft invoice would contain before anything is created.
      </Typography>
      <Typography>
        <strong>Create Draft Invoices</strong> only ever creates Draft
        invoices for review — a billing run never sends anything automatically.
        Running it again later for the same organisation only picks up
        journeys that haven't already been billed.
      </Typography>
      <Screenshot url="patient-transport/billing-runs.png">
        The Billing Runs view showing an organisation/period selection, the
        grouped preview table, and the Create Draft Invoices button.
      </Screenshot>
      <Typography>
        Dedicated duties set to <strong>whole-duty billing</strong> (see{' '}
        <DocLink url="/patient-transport/planning" text="Planning" docsPage />
        ) are billed through the duty itself: the run picks up each finished
        whole-duty duty in the period and prices it as one line — the fixed
        duty price, or duty hours × the hourly rate — noting how many
        journeys it carried. The journeys on such a duty are{' '}
        <strong>excluded from per-journey billing runs</strong> entirely, so
        they can never be charged twice.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Manually typed invoices don't always link their lines back to a
        journey, so billing runs err on the side of caution: any booking
        already covered by a manual invoice is left out of the run entirely,
        even if some of its journeys were never billed. That means a run will
        never double-bill, but bookings that have been part-invoiced by hand
        stay manual — check the preview and invoice any of their remaining
        journeys from the booking itself.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">PTS Invoices</Typography>
      <Typography>
        <DocLink url="/pts/invoices" text="PTS Invoices" /> is the tenant-wide
        invoice list — filter by status or organisation, update status, mark
        an invoice paid, issue a credit note, or select rows and export them
        to a CSV formatted for Xero's own sales-invoice import. The exported
        account code and tax types come from the Pricing &amp; Tariffs
        settings (default account code 200).
      </Typography>
      <Typography>
        Invoice statuses run Draft → Approved → Sent → Partially Paid / Paid
        (or Overdue). A Draft invoice can only move to{' '}
        <strong>Approved</strong> — it must be approved before it can be
        sent or marked paid.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Approving an invoice requires membership of the{' '}
        <strong>Finance</strong> team (or site admin) on top of the
        Co-Ordinator PTS permission. If the status change to Approved is
        rejected, that's the missing piece — everything else on this page
        needs only Co-Ordinator access.
      </Alert>
      <Screenshot url="patient-transport/pts-invoices.png">
        The PTS Invoices page in Invoices view, showing the status/
        organisation filters, the invoice grid with total/credited/paid/
        balance columns, and the Export to Xero button.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Credit notes</Typography>
      <Typography>
        Use <strong>Credit Note</strong> on any invoice to credit it in full,
        or credit specific lines/quantities for a partial credit. A running
        total of what's already been credited is shown, and AmbuNet won't let
        an invoice be credited for more than its original value.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Contract-level billing preferences</Typography>
      <Typography>
        Invoice grouping (per journey/booking/patient, or consolidated
        weekly/monthly), payment terms, PO requirements, whether a quote is
        required before planning, and SLA targets are all set per
        organisation — see{' '}
        <DocLink
          url="/patient-transport/organisations"
          text="Organisations and Users"
          docsPage
        />
        .
      </Typography>
    </Stack>
  </Stack>
)

export default Invoicing
